import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Link,
  Paper,
  CssBaseline,
  useMediaQuery,
  Button,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Header from '../../../shared/header/Header';
import GenerateKey from '../../../shared/developerKey/DeveloperKey';
import sampleImage from '../../../asset/images/sample.jpg';
import { LogPageViewToGA, LogTabEventToGA } from '../../../shared/googleAnalytics/GoogleAnalytics';
import {
  copyToClipboardClickedText,
  laipDocsLinkText,
  sdkContentLink,
  uuidContent,
} from '../../../shared/googleAnalytics/GoogleAnalyticsConstants';

const AgentCreationPublisher: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const scrollRef = useRef<HTMLElement>(null);
  const [positionFixed, setPositionFixed] = useState(false);

  const [activeSection, setActiveSection] = useState<string>('introduction');

  const indexItems = [
    { text: 'Introduction', href: '#introduction' },
    { text: 'Environment Setup', href: '#environment-setup' },
    { text: 'Repository Structure', href: '#repository-structure' },
    { text: 'Creating a New Agent', href: '#creating-agent' },
    { text: 'Creating Teams', href: '#creating-team' },
    { text: 'Creating Pull Requests', href: '#pull-requests' },
    { text: 'Video: How to create an Agent', href: '#video' },
    { text: 'Generate Developer Key', href: '#DK' },
  ];

  useEffect(() => {
    // Create a variable to store the current ref value
    const currentRef = scrollRef.current;

    const handleScrollPos = () => {
      if (currentRef) {
        const scrollTop = currentRef.scrollTop;
        setPositionFixed(scrollTop > 30);
      }
    };

    if (currentRef) {
      currentRef.addEventListener('scroll', handleScrollPos);
    }

    return () => {
      // Use the captured variable in the cleanup function
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScrollPos);
      }
    };
  }, []);

  useEffect(() => {
    const currentRef = scrollRef.current;
    if (!currentRef) return;

    const handleScroll = () => {
      // Only update activeSection if user has scrolled a bit
      if (currentRef.scrollTop < 10) {
        setActiveSection('introduction');
        return;
      }

      const sectionIds = [
        'introduction',
        'environment-setup',
        'repository-structure',
        'creating-agent',
        'creating-team',
        'pull-requests',
        'video',
        'DK',
      ];
      let current = sectionIds[0];
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el && currentRef) {
          const rect = el.getBoundingClientRect();
          const containerRect = currentRef.getBoundingClientRect();
          if (rect.top < containerRect.bottom - 80 && rect.bottom > containerRect.top + 80) {
            current = id;
          }
        }
      }
      if (Math.abs(currentRef.scrollHeight - currentRef.scrollTop - currentRef.clientHeight) < 2) {
        current = sectionIds[sectionIds.length - 1];
      }
      setActiveSection(current);
    };

    currentRef.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => currentRef.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <CssBaseline />
      <Header />
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="center"
        bgcolor="#f8f9fa"
        height={isMobile ? 'auto' : '100vh'}
        px={isMobile ? 1 : 3}
        pt={isMobile ? 8 : 12}
        ref={scrollRef}
        overflow="auto"
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            width: '100%',
            position: 'relative',
          }}
        >
          {/* Sidebar */}
          <Paper
            elevation={2}
            sx={{
              width: isMobile ? '100%' : 300,
              minWidth: isMobile ? '100%' : 'auto',
              bgcolor: '#f3f6fa',
              borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
              borderBottom: isMobile ? '1px solid #e0e0e0' : 'none',
              p: isMobile ? 2 : 3,
              mb: isMobile ? 2 : 0,
              position: isMobile ? 'static' : positionFixed ? 'fixed' : 'absolute',
              top: isMobile ? '0' : positionFixed ? '65px' : '0',
              left: isMobile ? 0 : positionFixed ? 0 : '-23px',
              bottom: 0,
              // ml: isDesktop ? (positionFixed ? '0' : '98px') : '0',
              [theme.breakpoints.between('sm', 'md')]: {
                width: 220,
                minWidth: 220,
                maxWidth: 220,
              },
              [theme.breakpoints.down('sm')]: {
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
              },
            }}
          >
            <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
              Contents
            </Typography>
            <Divider sx={{ marginY: 2, borderColor: '#1976d2' }} />
            <List disablePadding>
              {indexItems.map((item) => {
                const isActive = activeSection === item.href.slice(1);
                return (
                  <ListItem
                    key={item.href}
                    disableGutters
                    sx={{
                      mb: 1,
                      py: 0,
                      bgcolor: isActive ? '#1976d2' : item.text === 'Generate Developer Key' ? '#e3f2fd' : 'inherit',
                      color: isActive ? '#fff' : 'inherit',
                      borderRadius: 1,
                      transition: 'background 0.2s',
                    }}
                  >
                    <Link
                      href={item.href}
                      underline="hover"
                      color={isActive ? 'inherit' : 'primary'}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(item.href.slice(1))?.scrollIntoView({
                          behavior: 'smooth',
                        });
                        setActiveSection(item.href.slice(1)); // Add this line
                      }}
                      sx={{
                        width: '100%',
                        display: 'block',
                        px: 1.5,
                        color: isActive ? '#fff' : 'inherit',

                        borderRadius: 1,
                        transition: 'background 0.2s, color 0.2s',
                      }}
                    >
                      <ListItemText
                        primary={item.text}
                        sx={{
                          fontWeight: item.text === 'Generate Developer Key' ? 'bold' : isActive ? 'bold' : 'normal',
                        }}
                      />
                    </Link>
                  </ListItem>
                );
              })}
            </List>
          </Paper>

          {/* Main content */}
          <Box
            component="main"
            flex={1}
            sx={{
              marginLeft: { xs: 0, sm: '220px', md: '300px' },
              width: { xs: '100%', sm: `calc(100% - 220px)`, md: `calc(100% - 300px)` },
              [theme.breakpoints.down('sm')]: {
                marginLeft: 0,
                width: '100%',
              },
              [theme.breakpoints.between('sm', 'md')]: {
                marginLeft: '220px',
                width: 'calc(100% - 220px)',
              },
              [theme.breakpoints.up('md')]: {
                marginLeft: '277px',
                width: 'calc(100% - 277px)',
              },
              // height: '100vh',
              // overflowY: 'auto',
            }}
          >
            <Box
              sx={{
                p: isMobile ? 2 : 5,
                bgcolor: '#fff',
                borderRadius: 2,
                boxShadow: 2,
                // paddingTop: isMobile ? '15px' : '25px',
              }}
            >
              <Typography variant={isMobile ? 'h5' : 'h4'} color="primary" fontWeight="bold" gutterBottom>
                Agent Development Guide
              </Typography>
              <Divider sx={{ marginY: 2 }} />

              <Section id="introduction" title="Introduction">
                <Typography paragraph>
                  This user guide provides detailed instructions on creating agents using APIs. The guide aims to help
                  software developers to create agents successfully.
                </Typography>
              </Section>

              <Section id="environment-setup" title="Setting up the Environment">
                <Typography paragraph>
                  Install and set up the necessary development tools, libraries, and dependencies for agent development,
                  ensuring compatibility with the specified environment.
                </Typography>
                <StepList
                  steps={[
                    {
                      title: 'Install Python and IDE',
                      content:
                        'Start by installing Python 3.11, recommended IDE as PyCharm or VS Code, and local testing software as Postman (for all OS) or Insomnia (for mac) (by raising a service request on service now)',
                    },
                    {
                      title: 'Download SDK Files',
                      content: (
                        <>
                          Download and extract provided files from{' '}
                          <Link
                            href="/sdk_repository.zip"
                            underline="hover"
                            color="primary"
                            download
                            onClick={() => LogTabEventToGA(`${sdkContentLink}: SDK repository`)}
                          >
                            SDK repository.
                          </Link>{' '}
                          Take the “agent folder” or “team folder” along with the whl file and move them to a new
                          “project folder” on pc. In case Mac users are not able to access .whl file inside its folder,
                          use “command shift .” to view file
                        </>
                      ),
                    },
                    {
                      title: 'Create New Project',
                      content:
                        "Open PyCharm, go to File → New Project, provide the project folder's path and a project name.",
                    },
                    {
                      title: 'Create Virtual Environment',
                      content: (
                        <>
                          <Typography sx={{ mb: 1 }}>
                            Create a virtual environment {'>'} Go to terminal from pycharm {'>'} run below commands:
                          </Typography>

                          <Command>python -m venv .venv</Command>
                        </>
                      ),
                    },
                    {
                      title: 'Activate a virtual environment:',
                      content: (
                        <>
                          <Typography sx={{ mb: 1 }}>For macOS/Linux:</Typography>
                          <Command>source .venv/bin/activate</Command>
                          <Typography sx={{ mb: 1 }}>For Windows:</Typography>
                          <Command>.venv\Scripts\activate</Command>
                        </>
                      ),
                    },
                    {
                      title: 'Use the package manager',
                      content: (
                        <>
                          <Typography sx={{ mb: 1 }}>pip to install the package. Run below commands</Typography>
                          <Command>pip install &lt;path to .whl file&gt;</Command>
                          <Command>pip install -r &lt;path to requirements.txt&gt;</Command>
                        </>
                      ),
                    },
                    {
                      title: 'Before running main.py, user has to export env in their terminal using below command',
                      content: (
                        <>
                          <Command>export REQUESTS_CA_BUNDLE=""</Command>
                        </>
                      ),
                    },
                    {
                      title: 'Run main.py using below command, in the same terminal',
                      content: (
                        <>
                          <Command>python &lt;path to main.py&gt;</Command>
                          User can learn more about Autogen extension by referring to{' '}
                          <Link
                            href="https://docs.waip.wiprocms.com/"
                            underline="hover"
                            color="primary"
                            target="_blank"
                            onClick={() => LogPageViewToGA(laipDocsLinkText)}
                          >
                            {' '}
                            WAIP docs{' '}
                          </Link>{' '}
                        </>
                      ),
                    },
                  ]}
                />
              </Section>

              <Section id="repository-structure" title="Understanding Repository Structure">
                <Typography paragraph>
                  The structure of the marketplace agent to be committed includes the main.py, models.py,
                  requirements.txt, run_workflow.py, config.py, config.json, and docker file. Read below to understand
                  the role of each file in the agent creation process:
                </Typography>
                <FileStructure
                  files={[
                    {
                      name: 'main.py',
                      description:
                        'Here we have defined a /execute end point. Please note that apart from testing purposes, UI does not communicate with agents directly, it gets authenticated first by a middle layer, then it calls the execute end point. Upon being invoked the endpoint will call run_workflow.py. This file also provides a sample invocation for the server',
                    },
                    {
                      name: 'models.py',
                      description:
                        'Contains structure of input to the endpoint. This code defines a data model for an ExecuteRequest with optional string fields for inputs such as user_id, tenant_id, session_id, content, api_key, and dataset_id, allowing them to be None if not provided. ',
                    },
                    {
                      name: 'requirements.txt',
                      description:
                        'Contains extra requirements to be installed on system that is fastapi and to run it uvicorn',
                    },
                    {
                      name: 'run_workflow.py',
                      description: (
                        <>
                          <Typography sx={{ mb: 2 }} paragraph>
                            Run workflow uses the API key supplied in the input request to create an agent or a team.
                            The base URL for Lab45 Platform calls defaults to{' '}
                            <Link href="https://api.waip.wiprocms.com/v1.1/" color="primary" target="_blank" underline="hover">
                              WAIP,
                            </Link>{' '}
                            can be overriden using environment variable base_url.
                          </Typography>
                        </>
                      ),
                    },
                    {
                      name: 'config.py',
                      description:
                        "This code is a Python script that reads the file named 'config.json'. It loads the content of the file into the CONFIG_DATA dictionary. If the file is not found or if its content cannot be decoded as JSON, it raises an HTTPException with an appropriate error message and status code.",
                    },
                    {
                      name: 'config.json',
                      description:
                        'This JSON code details unique identifiers, description, technology stack, and ownership. It specifies access permissions, last updated date, and includes placeholders for user and tenant IDs, as well as an API key.',
                    },
                    {
                      name: '.Dockerfile',
                      description:
                        'This Dockerfile sets up a Python environment using the slim version of Python 3.11, installs a specific Python package from a wheel file, copies application files into the Docker image, installs dependencies listed in requirements.txt, exposes defined port, and runs the application using uvicorn.',
                    },
                  ]}
                />
              </Section>

              <Section id="creating-agent" title="Creating a New Agent">
                <Typography paragraph>
                  Two example implementations are provided that create a general-purpose agent or team. You can use
                  these as templates and make modifications.
                </Typography>
                <StepList
                  steps={[
                    {
                      title: 'No Changes to Certain Files',
                      content: 'No changes are expected to these three files: main.py, models.py, and config.py',
                    },
                    {
                      title: 'Open requirements.txt ',
                      content:
                        'If your agent needs additional dependencies, add them to requirements.txt. The required libraries can then be imported in run_workflow.py.',
                    },
                    {
                      title: 'Open run_workflow.py file',
                      content:
                        'The function get_agent() or get_team() are the functions which need to be edited and will contain the logic for the agentic usecase along with the definition of the agent or team used. Edit name and system message (agent description). User can also add tools and attach with the agents.',
                    },
                    {
                      title: 'Update config.json file',
                      content: (
                        <>
                          <Typography paragraph>
                            Enter the details specified in angle brackets &lt;&gt;, keeping the rest of the code as it
                            is. In some cases, you might want to change parameters such as the tech stack and port
                            exposed, which they can definitely pursue.
                          </Typography>
                          <Typography paragraph>
                            <strong>
                              To generate <code>party_id</code> (UUID):
                            </strong>
                            <ul style={{ listStyleType: 'none' }}>
                              <li>
                                Go to{' '}
                                <Link
                                  href="https://www.uuidgenerator.net/"
                                  color="primary"
                                  target="_blank"
                                  underline="hover"
                                  onClick={() => LogTabEventToGA(`${uuidContent}: UUID Generator`)}
                                >
                                  UUID Generator
                                </Link>
                                .
                              </li>
                              <li>
                                Click &quot;Generate UUID&quot; and copy the generated value (e.g.,{' '}
                                <code>ecc10845-54ba-42d7-9706-5d71365e5b15</code>
                                ).
                              </li>
                            </ul>
                          </Typography>

                          <Typography sx={{ mb: 1 }}>
                            Note that api key must be entered only for running in local. Also, in case user mentions
                            required files (files are the ones from user’s local) as true, then include a line as shown
                            below:
                          </Typography>

                          <Command>
                            &quot;supports_file_upload&quot;:&lt;false or true&gt;,
                            <br />
                            &quot;file_format_supported&quot; : [&quot;.png&quot;, &quot;.jpg&quot;, &quot;.jpeg&quot;],
                          </Command>

                          <Typography sx={{ mb: 1 }}>or in case not from image category, use below:</Typography>
                          <Command>
                            &quot;supports_file_upload&quot;:&lt;false or true&gt;,
                            <br />
                            &quot;file_format_supported&quot; : [&quot;.pdf&quot;, &quot;.docx&quot;,
                            &quot;.xlsx&quot;],
                          </Command>

                          {/* <Alert title="Generating IDs">
                            <Typography paragraph>
                              <strong>party_id or uuid</strong>: Generate using
                              a UUID generator, e.g.,
                              ecc10845-54ba-42d7-9706-5d71365e5b15.
                            </Typography>
                            <Typography paragraph>
                              <strong>api_key or developer key</strong>:
                              Generate by accessing LAIP → Profile icon → Enter
                              name → Generate key → Copy key.
                            </Typography>
                          </Alert> */}
                        </>
                      ),
                    },
                    {
                      title: 'Open .Dockerfile',
                      content: (
                        <>
                          This user needs to update two things: folder name and port in docker file. If agent requires
                          environment variables or any other dependency, it needs to be updated as well in docker file.
                          Note that port must match with the port in files config.json and main.py.
                        </>
                      ),
                    },
                    {
                      title: 'So overall, in case of datasets and files being included, user should: ',
                      content: (
                        <>
                          <Typography>
                            Keep optional part in models.py {'>'} pass in execute workflow in main.py {'>'} read via
                            kwargs in run_workflow.py
                          </Typography>
                          <Typography>Note:</Typography>
                          <Typography>
                            If you want to use the Platform RAG tool, you must pre-upload your files and obtain a
                            dataset_id, and have to be pre-trained from WAIP. The dataset_id should be passed in the
                            payload when making a request.
                          </Typography>
                          <Typography>
                            In your config file, add the following fields to indicate dataset support and
                            environment-specific dataset IDs:
                          </Typography>
                          <Command>
                            {`{
  "supports_dataset": true,
  "datasetIds": {
    "dev": "<enter dev dataset id>",
    "staging": "<enter staging dataset id>",
    "prod": "<enter prod dataset id>"
}
}`}
                          </Command>
                          <Typography>
                            Set "supports_dataset" to true if your agent supports RAG/dataset usage. Fill in the
                            appropriate datasetIds for each environment if needed, or leave them blank if you will
                            always pass the dataset_id in the payload. This setup allows dynamic selection of datasets
                            per request while keeping environment-specific configuration in your config file.
                            <Command>
                              {`
 { "supports_dataset": true, "datasetIds": { "dev": "", "staging": "", "prod": "" } }
`}
                            </Command>
                            Set `"supports_dataset"` to `true` if your agent supports RAG/dataset usage. Fill in the
                            appropriate `datasetIds` for each environment if needed, or leave them blank if you will
                            always pass the `dataset_id` in the payload.
                          </Typography>
                        </>
                      ),
                    },
                    {
                      title: 'Run agent locally',
                      content: (
                        <>
                          <Typography paragraph>
                            Every agent will have to be run on a unique port that was entered earlier in main.py, refer
                            that port
                          </Typography>
                          <Typography sx={{ mb: 1 }}>
                            Postman {'>'} open respective agent’s POST {'>'} enter url http://0.0.0.0:&lt;port of
                            main.py say 8001&gt;/execute {'>'} Enter payload that is below code piece in “Body” as json
                          </Typography>

                          <Command>
                            {`{
  "party_id": "<enter uuid>",
  "message": "<user query>",
  "session_id": "<enter uuid>",
  "dataset_id": "<enter dataset id if reqd>",
  "api_key": "<your api key>"
}`}
                          </Command>
                          <Typography paragraph>
                            Click "Send" to generate response and consider 200 OK as successful output generation.
                          </Typography>
                          <Typography sx={{ mb: 1 }}>Here is a sample output:</Typography>
                          <img src={sampleImage} alt="Sample Output" width={'100%'} />
                        </>
                      ),
                    },
                  ]}
                />
              </Section>

              <Section id="creating-team" title="Creating Teams">
                <Typography paragraph>Termination and Chat Management (in case of team creation)</Typography>
                <ul>
                  <li>
                    Creating RoundRobinGroupChat: the existing template covers how to instantiate multiple agents. User
                    can add their multiple agents here. User can utilise other group chat feature from autogen, for that
                    they can refer{' '}
                    <Link href="https://api.waip.wiprocms.com/v1.1/" color="primary" target="_blank" underline="hover">
                      WAIP
                    </Link>{' '}
                    autogen documentation.
                  </li>
                  <li>
                    Chat termination can be done by searching for mention of a specific text, or a max message count.
                    User can change the max message count based on the conversation limit between agents they prefer.
                  </li>
                </ul>
              </Section>

              <Section id="pull-requests" title="Creating Pull Requests">
                <StepList
                  steps={[
                    {
                      title: 'User with Github access',
                      content: (
                        <>
                          <Typography>To clone the repo, run the following command:</Typography>
                          <Command>git clone https://github.com/Lab45-CoreAI/Marketplace-agents.git</Command>
                          <Typography>
                            Once the repo is cloned in the main branch, users need to create their local branch:
                          </Typography>
                          <Command>git checkout -b "&lt;branch name&gt;"</Command>
                          <Typography>Run the following commands:</Typography>
                          <Command>git add .</Command>
                          <Command>git commit -m "&lt;commit message&gt;"</Command>
                          <Command>git push origin &lt;branch name&gt;</Command>
                          <Typography>
                            Users have to navigate to GitHub, click on Create PR, and tick mark the list of requisites
                            as per progress.
                          </Typography>
                        </>
                      ),
                    },
                  ]}
                />
              </Section>

              <Section id="video" title="Video: How to create an Agent">
                <Box sx={{ minHeight: 400 }}>
                  <Typography gutterBottom>
                    This video tutorial demonstrates how publishers can create agents on their local machine.
                  </Typography>
                  <video width={isMobile ? '100%' : 600} controls>
                    <source
                      src="https://lab45ftp.blob.core.windows.net/agents-hosting-guide/videos/Agent_creation.mov"
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                </Box>
              </Section>
              <Section id="DK" title="Generate Developer Key">
                <GenerateKey />
              </Section>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const Section: React.FC<{
  id: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, title, children }) => (
  <Box id={id} mb={5}>
    <Typography variant="h6" fontWeight="bold" gutterBottom>
      {title}
    </Typography>
    <Box component="div" sx={{ pl: 3 }}>
      {children}
    </Box>
  </Box>
);

const StepList: React.FC<{
  steps: { title: string; content: React.ReactNode }[];
}> = ({ steps }) => (
  <ol>
    {steps.map((step, index) => (
      <li key={index}>
        <Typography variant="h6" gutterBottom>
          {step.title}
        </Typography>
        <Typography paragraph>{step.content}</Typography>
      </li>
    ))}
  </ol>
);

const Command: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    LogTabEventToGA(copyToClipboardClickedText);
    navigator.clipboard.writeText(children as string);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: '#333',
        color: '#fff',
        p: 2,
        borderRadius: 1,
        mb: 2,
        overflowX: 'auto',
      }}
    >
      <Button
        onClick={copyToClipboard}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          color: '#fff',
          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
        }}
      >
        {copied ? 'Copied!' : 'Copy'}
      </Button>
      <pre>{children}</pre>
    </Box>
  );
};

const FileStructure: React.FC<{
  files: { name: string; description: React.ReactNode }[];
}> = ({ files }) => (
  <Box sx={{ p: 3, borderRadius: 1, mb: 3 }}>
    {files.map((file, index) => (
      <Box key={index} mb={2}>
        <Typography variant="h6" gutterBottom>
          {file.name}
        </Typography>
        <Typography variant="body2">{file.description}</Typography>
      </Box>
    ))}
  </Box>
);

// const Alert: React.FC<{ title?: string; children: React.ReactNode }> = ({
//   title,
//   children,
// }) => (
//   <Box
//     sx={{
//       borderLeft: "4px solid #2196f3",
//       bgcolor: "#e3f2fd",
//       p: 2,
//       borderRadius: 1,
//       mb: 2,
//     }}
//   >
//     {title && (
//       <Typography variant="h6" gutterBottom>
//         {title}
//       </Typography>
//     )}
//     {children}
//   </Box>
// );

export default AgentCreationPublisher;
